import type { HTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  lines?: number;
  /** Renders a circle instead of bars — for avatar and media slots. */
  circle?: boolean;
}

export interface SkeletonRegionProps extends HTMLAttributes<HTMLDivElement> {
  /** Names the region in the single polite announcement, e.g. "order list". */
  region: string;
  children: React.ReactNode;
}

/**
 * PRM-18 Skeleton — a structural loading placeholder at the shape of the
 * resolved content, never a centred spinner on a full page (07-02 state matrix).
 * Not permitted in `floor`; floor shows a text state instead.
 */
export function Skeleton({ lines = 1, circle, className, ...props }: SkeletonProps) {
  return (
    <div
      data-primitive="PRM-18"
      aria-hidden="true"
      className={cn('flex flex-col gap-xs [[data-theme=floor]_&]:hidden', className)}
      {...props}
    >
      {Array.from({ length: circle ? 1 : lines }, (_, i) => (
        <span
          key={i}
          className={cn(
            'block animate-pulse bg-sunken',
            circle ? 'h-10 w-10 rounded-pill' : 'h-4 rounded-control',
            !circle && i === lines - 1 && lines > 1 && 'w-3/5',
          )}
        />
      ))}
    </div>
  );
}

/**
 * Wraps a skeleton so the region announces once, politely, rather than each bar
 * announcing separately (07-11 per-component criteria, PRM-18).
 */
export function SkeletonRegion({ region, children, className, ...props }: SkeletonRegionProps) {
  return (
    <div className={cn('contents', className)} {...props}>
      <span role="status" aria-live="polite" className="sr-only">
        Loading {region}
      </span>
      {children}
    </div>
  );
}
